import React, { useMemo } from 'react';
import Avatar from '@atlaskit/avatar';
import { HeadingItem } from '@atlaskit/menu';
import { PROJECTS_ITEM_TYPE } from '@atlassian/jira-favourite-change-provider/src/model/constants.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	getBeforeEntity,
	getLinkFromProject,
} from '@atlassian/jira-navigation-apps-common/src/utils/index.tsx';
import { testIdConcat } from '@atlassian/jira-navigation-apps-common/src/utils/test-id.tsx';
import { DEFAULT_LOAD_COUNT_RECENT } from '../../../../common/constants.tsx';
import { FavoriteButton } from '../../../../common/ui/menu/favourite-button/main.tsx';
import { MenuItem } from '../../../../common/ui/menu/item/index.tsx';
import { MenuList, MenuListItem } from '../../../../common/ui/menu/list/index.tsx';
import { useProjectNavItems as useItems } from '../../../../controllers/items/index.tsx';
import messages from './messages.tsx';
import type { RecentContentProps } from './types.tsx';

export const RecentContent = ({ testIdPrefix }: RecentContentProps) => {
	const { formatMessage } = useIntl();
	const { recentItems, favoriteItems, changeFavorite: onChangeFavorite, changedItems } = useItems();
	const itemsToShow = useMemo(() => recentItems.slice(0, DEFAULT_LOAD_COUNT_RECENT), [recentItems]);

	const testId = testIdConcat(testIdPrefix, 'items-recent');

	return recentItems.length > 0 ? (
		<span data-testid={testId}>
			<HeadingItem testId={testIdConcat(testId, 'heading')}>
				{formatMessage(messages.recent)}
			</HeadingItem>
			<MenuList>
				{itemsToShow.map((item, idx) => {
					const { url, id, favourite: isFavourite, metadata, title } = item;
					const contextItem = changedItems[item.id.toString()];
					const isPending =
						contextItem && contextItem.pending === true ? contextItem.pending : false;
					const { id: beforeEntityId, type: beforeEntityType } = getBeforeEntity(favoriteItems);

					const { params, query, route: to } = getLinkFromProject(item);

					return (
						<MenuListItem key={id} ariaLabel={formatMessage(messages.recent)}>
							<MenuItem
								analytics={{
									actionSubjectId: 'projectsNavigationMenuItem',
									menuItemId: id,
									menuItemType: 'recent',
									position: idx,
									starred: Boolean(isFavourite),
								}}
								description={metadata}
								href={url}
								iconBefore={
									<Avatar
										appearance="square"
										size="small"
										borderColor="transparent"
										src={item.avatarUrl}
									/>
								}
								iconAfter={
									<FavoriteButton
										beforeEntityId={beforeEntityId}
										beforeEntityType={beforeEntityType}
										id={String(id)}
										isFavorite={Boolean(isFavourite)}
										favoriteItemName={title}
										isPending={isPending}
										menuItemType="recent"
										onChangeFavorite={onChangeFavorite}
										type={PROJECTS_ITEM_TYPE}
									/>
								}
								params={params}
								query={query}
								testId={testIdConcat(testId, `item-${idx}`)}
								to={to}
								aria-label={`${title} ${metadata || ''}`}
							>
								{title}
							</MenuItem>
						</MenuListItem>
					);
				})}
			</MenuList>
		</span>
	) : null;
};
